import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { NavLink, Outlet, Link, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, LogIn, User, Menu, Package, Home as HomeIcon, X, BadgePercent, Heart, LogOut, ChevronDown, Wallet, Sparkles, MapPin, LifeBuoy } from "lucide-react";
import { Slider } from "../components/ui/slider";
import { useAuth } from "./AuthContext";
import AuthOptionsModal from "./AuthOptionsModal";
import { useCart } from "./CartContext";
import { useWishlist } from "./WishlistContext";
import Footer from "../components/shop/Footer";
import DepartmentNav from "../components/shop/DepartmentNav";
import AIStylistFab from "../components/shop/ai/AIStylistFab";
import WelcomePopup from "../components/shop/marketing/WelcomePopup";
import MegaMenu from "../components/shop/MegaMenu";
import HeaderTrustStrip from "../components/shop/HeaderTrustStrip";
import { SIZE_ORDER, getDisplaySizes } from "../lib/sizes";
import NotificationBell from "../components/NotificationBell";
import IOSInstallHint from "../components/IOSInstallHint";
import InstallPrompt from "../components/shop/InstallPrompt";
import AddedCrossSellModal from "../components/shop/cart/AddedCrossSellModal";
import useBrandAsset from "../lib/useBrandAsset";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom Studio header CTA — only renders when admin has flipped the master
// feature flag ON. Hidden silently otherwise.
// On desktop (md+): pill in the header with the full "Create custom outfit"
// label. On mobile: a compact sparkle icon-only button so the entry stays
// always visible from every page without crowding the small header (Bundle D).
function CustomStudioHeaderCTA() {
    const { user } = useAuth();
    const isAuthed = !!user?.user_id;
    const [show, setShow] = useState(false);
    const [count, setCount] = useState(0);
    const [serverCount, setServerCount] = useState(0);
    // iter347 (Mar 1 2026) — Only run the attention-bounce animation
    // when the user is NOT already inside /custom-studio. Once they're
    // inside the studio, the bounce on the header pill becomes a
    // distracting micro-animation that pulls focus away from the
    // builder itself.
    const loc = useLocation();
    const insideStudio = loc.pathname.startsWith("/custom-studio");
    const bounceClass = insideStudio ? "" : "cs-attention-bounce";
    useEffect(() => {
        axios.get(`${API}/custom-studio/state`)
            .then(r => setShow(!!r.data?.show_header_cta))
            .catch(() => setShow(false));
    }, []);

    // Server-side saved designs are part of the badge too when the customer
    // is signed in. We refresh on the same custom event drafts dispatch.
    //
    // iter323 (Feb 27 2026) — Skip the call entirely for anon customers
    // to keep the browser console clean of expected 401s.
    useEffect(() => {
        if (!isAuthed) {
            setServerCount(0);
            return undefined;
        }
        async function reload() {
            try {
                const r = await axios.get(`${API}/custom-studio/designs`, { validateStatus: (s) => s < 500 });
                if (r.status === 200) setServerCount((r.data?.rows || []).length);
                else setServerCount(0);
            } catch { setServerCount(0); }
        }
        reload();
        window.addEventListener("jns:custom-studio-changed", reload);
        return () => window.removeEventListener("jns:custom-studio-changed", reload);
    }, [isAuthed]);

    // Track draft + saved-guest counts on this device for the badge. We poll
    // a "storage" event so cross-tab edits keep the badge accurate, and also
    // re-read on route changes via a custom event the studio fires after save.
    useEffect(() => {
        function recount() {
            let n = 0;
            try {
                const raw = localStorage.getItem("jns_custom_in_progress_drafts");
                if (raw) {
                    const arr = JSON.parse(raw);
                    if (Array.isArray(arr)) n += arr.filter(d => d && d.draft && (d.draft.fabric || d.draft.style)).length;
                }
            } catch { /* noop */ }
            try {
                const raw = localStorage.getItem("jns_guest_custom_designs");
                if (raw) {
                    const arr = JSON.parse(raw);
                    if (Array.isArray(arr)) n += arr.length;
                }
            } catch { /* noop */ }
            setCount(n);
        }
        recount();
        window.addEventListener("storage", recount);
        window.addEventListener("jns:custom-studio-changed", recount);
        const id = setInterval(recount, 4000);
        return () => {
            window.removeEventListener("storage", recount);
            window.removeEventListener("jns:custom-studio-changed", recount);
            clearInterval(id);
        };
    }, []);

    const totalCount = count + serverCount;

    if (!show) return null;
    return (
        <>
            {/* Desktop: iter341 — adopted the same wine/burgundy gradient +
                attention-bounce treatment as the landing hero's "My designs"
                pill so the entry point is unmissable from every storefront
                page. cs-hero-cta-mini = the shimmering wine gradient,
                cs-attention-bounce = the gentle hop animation. */}
            <Link
                to="/custom-studio"
                className={`cs-hero-cta-mini ${bounceClass} hidden md:inline-flex relative items-center gap-1.5 h-9 px-3 lg:px-4 mr-1 rounded-full text-ivory text-[13px] font-medium tracking-wide whitespace-nowrap shadow-md hover:shadow-lg transition-shadow`}
                data-testid="header-custom-studio-cta"
                title="Design a made-for-you boutique outfit"
            >
                <Sparkles size={13} className="text-ivory flex-shrink-0 cs-cta-spark" />
                <span className="cs-cta-text">Your Custom Studio</span>
                {totalCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-ivory text-wine text-[10px] font-bold flex items-center justify-center ring-2 ring-wine/40 shadow-sm" data-testid="header-cta-count">{totalCount}</span>
                )}
            </Link>
            {/* Mobile: iter341 — same wine gradient + attention-bounce, but a
                compact pill with sparkle icon + "Custom" label so it doesn't
                crowd the small header. */}
            <Link
                to="/custom-studio"
                className={`cs-hero-cta-mini ${bounceClass} md:hidden relative inline-flex items-center gap-1.5 h-9 pl-2 pr-2.5 rounded-full text-ivory flex-shrink-0 shadow-md`}
                data-testid="header-custom-studio-cta-mobile"
                aria-label="Create custom outfit"
                title="Design a made-for-you boutique outfit"
            >
                <Sparkles size={13} className="text-ivory cs-cta-spark flex-shrink-0" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] cs-cta-text">Custom</span>
                {totalCount > 0 ? (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-ivory text-wine text-[9px] font-bold flex items-center justify-center ring-2 ring-wine/40" data-testid="header-cta-count-mobile">{totalCount}</span>
                ) : (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-300 ring-1 ring-wine cs-pulse-dot" />
                )}
            </Link>
        </>
    );
}

// Mobile-only promotional strip pinned below the header on every customer
// page. Boutique-toned gradient so it reads as a feature highlight, not a
// banner ad. Dismissible per-session via the close icon so customers who
// don't want it can hide it for the visit (Bundle D). Hidden:
//   • when admin master flag is OFF
//   • when the user is already inside /custom-studio*
//   • on lg+ screens (desktop has the header pill + bottom-right floating FAB)
//   • when the user has dismissed it this session
// eslint-disable-next-line no-unused-vars -- retired from render (Phase 2), kept for future campaigns
function CustomStudioMobileStrip() {
    const [show, setShow] = useState(false);
    const [dismissed, setDismissed] = useState(() => {
        try { return sessionStorage.getItem("cs_strip_dismissed") === "1"; } catch { return false; }
    });
    const loc = useLocation();

    useEffect(() => {
        axios.get(`${API}/custom-studio/state`)
            .then(r => setShow(!!r.data?.show_header_cta))
            .catch(() => setShow(false));
    }, []);

    if (!show || dismissed) return null;
    if (loc.pathname.startsWith("/custom-studio")) return null;

    function dismiss(e) {
        e.preventDefault(); e.stopPropagation();
        try { sessionStorage.setItem("cs_strip_dismissed", "1"); } catch {/* noop */}
        setDismissed(true);
    }

    return (
        <Link
            to="/custom-studio"
            className="lg:hidden relative flex items-center gap-3 px-4 py-3 overflow-hidden border-b border-rosegold/25 bg-gradient-to-r from-[#1A1818] via-[#2A2120] to-[#1A1818] text-ivory shadow-[0_2px_8px_-2px_rgba(26,24,24,0.25)]"
            data-testid="custom-studio-mobile-strip"
        >
            {/* Animated rosegold sheen sweeping right-to-left */}
            <span aria-hidden="true" className="cs-strip-sheen absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-rosegold/30 to-transparent pointer-events-none" />
            <span className="relative w-9 h-9 rounded-full bg-gradient-to-br from-rosegold to-rosegold-deep flex items-center justify-center flex-shrink-0 shadow-md ring-1 ring-rosegold/40">
                <Sparkles size={15} className="text-ivory" />
            </span>
            <div className="relative flex-1 min-w-0">
                <div className="text-[9.5px] uppercase tracking-[0.2em] text-rosegold leading-none">New · made-for-you</div>
                <div className="text-[13px] font-medium text-ivory leading-tight mt-1 truncate">Design your custom outfit</div>
            </div>
            <span className="relative inline-flex items-center gap-1 text-[11px] font-semibold whitespace-nowrap text-ink bg-ivory rounded-full px-3 py-1.5 shadow-sm">
                Start <ChevronDown size={11} className="-rotate-90" />
            </span>
            <button onClick={dismiss} className="relative p-1 -mr-1 rounded-full hover:bg-white/10 text-ivory/70 hover:text-ivory flex-shrink-0 transition-colors" aria-label="Dismiss" data-testid="custom-studio-mobile-strip-dismiss">
                <X size={14} />
            </button>
        </Link>
    );
}

function CustomStudioFloatingCTA() {
    // Desktop floating FAB removed per user feedback — header pill + mobile
    // promo strip are sufficient entry points across the customer portal.
    return null;
}

// Read all available sizes from current item list (broadcast by ShopHome)
function useAvailableSizes() {
    const [sizes, setSizes] = useState(() => {
        try { return (typeof window !== "undefined" && window.__jns_available_sizes) || []; } catch { return []; }
    });
    useEffect(() => {
        const handler = (e) => setSizes(e.detail || []);
        window.addEventListener("jns:available-sizes", handler);
        return () => window.removeEventListener("jns:available-sizes", handler);
    }, []);
    return sizes;
}

function useMaxPrice() {
    const [max, setMax] = useState(() => {
        try {
            const v = Number((typeof window !== "undefined" && window.__jns_max_price) || 0);
            return v > 0 ? Math.ceil(v / 100) * 100 : 5000;
        } catch { return 5000; }
    });
    useEffect(() => {
        const handler = (e) => {
            const v = Number(e.detail || 0);
            if (v > 0) setMax(Math.ceil(v / 100) * 100);
        };
        window.addEventListener("jns:max-price", handler);
        return () => window.removeEventListener("jns:max-price", handler);
    }, []);
    return max;
}

function FilterPanel({ categories, sp, setSp, navTo, currentPath }) {
    const cat = sp.get("cat") || "All";
    const sizes = (sp.get("sizes") || "").split(",").filter(Boolean);
    const availableSizes = useAvailableSizes();
    const maxPrice = useMaxPrice();
    const min = Number(sp.get("minP") || 0);
    const max = Number(sp.get("maxP") || maxPrice);
    const [range, setRange] = useState([min, max]);

    // Determine current category from URL (e.g. /c/Frock) so the active state highlights correctly
    const urlCategory = (() => {
        if (currentPath && currentPath.startsWith("/c/")) {
            try { return decodeURIComponent(currentPath.slice(3).split("/")[0]); } catch { return ""; }
        }
        return null;
    })();
    const activeCat = urlCategory || cat;
    const isSalePage = currentPath === "/sale";

    useEffect(() => {
        setRange([Number(sp.get("minP") || 0), Number(sp.get("maxP") || maxPrice)]);
    }, [maxPrice, sp]);

    // Filter-style param updates (price/size) — stay on current page, just update URL params
    const updateParams = (patch) => {
        const next = new URLSearchParams(sp);
        for (const [k, v] of Object.entries(patch)) {
            if (v === null || v === undefined || v === "" || v === "All") next.delete(k);
            else next.set(k, v);
        }
        setSp(next, { replace: true });
    };

    // Category clicks always navigate to /c/:category (or "/" for All)
    const selectCategory = (name) => {
        if (!name || name === "All") {
            if (navTo) navTo("/"); else window.location.assign("/");
        } else {
            const url = `/c/${encodeURIComponent(name)}`;
            if (navTo) navTo(url); else window.location.assign(url);
        }
    };

    // On Sale → /sale (toggle off if already there)
    const toggleSale = () => {
        if (isSalePage) {
            if (navTo) navTo("/"); else window.location.assign("/");
        } else {
            if (navTo) navTo("/sale"); else window.location.assign("/sale");
        }
    };

    const toggleSize = (s) => {
        const set = new Set(sizes);
        if (set.has(s)) set.delete(s); else set.add(s);
        updateParams({ sizes: Array.from(set).join(",") });
    };

    const clearAll = () => { if (navTo) navTo("/"); else setSp(new URLSearchParams(), { replace: true }); };
    const hasActive = activeCat !== "All" || sizes.length > 0 || sp.get("minP") || sp.get("maxP") || isSalePage;

    return (
        <>
            <button
                onClick={toggleSale}
                data-testid="filter-on-sale"
                className={`w-full mb-4 flex items-center gap-2 px-3 py-2.5 text-sm font-semibold transition-colors uppercase tracking-wider ${
                    isSalePage
                        ? "bg-wine text-ivory"
                        : "bg-ivory-soft text-wine hover:bg-rosegold/20"
                }`}
            >
                <BadgePercent size={15} /> On Sale
            </button>

            <div className="flex items-center justify-between mb-3">
                <p className="overline">Refine</p>
                {hasActive && (
                    <button onClick={clearAll} className="text-[11px] text-rosegold hover:underline" data-testid="clear-filters">Clear all</button>
                )}
            </div>

            <div className="border-t border-border pt-5 mb-6" data-testid="filter-price">
                <p className="overline mb-3">Price</p>
                <Slider
                    value={range}
                    onValueChange={setRange}
                    onValueCommit={(v) => updateParams({ minP: v[0] > 0 ? v[0] : null, maxP: v[1] < maxPrice ? v[1] : null })}
                    min={0}
                    max={maxPrice}
                    step={50}
                    className="mb-2"
                />
                <div className="flex justify-between text-[11px] text-ink-soft">
                    <span>₹{range[0].toLocaleString("en-IN")}</span>
                    <span>₹{range[1].toLocaleString("en-IN")}</span>
                </div>
            </div>

            {/* Size filter removed per design — sizes can be picked on PDP */}
        </>
    );
}

export default function ShopLayout() {
    const { user, loading, logout, openAuthModal, closeAuthModal, authModalOpen } = useAuth();
    const { count } = useCart();
    const { count: wlCount } = useWishlist();
    const loc = useLocation();
    const navigate = useNavigate();
    const [sp, setSp] = useSearchParams();
    const [cats, setCats] = useState([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    // Jul 2026 — boutique WhatsApp number for the drawer's support link.
    // Same public settings the footer uses; fetched once per layout mount.
    const [waNumber, setWaNumber] = useState("");
    useEffect(() => {
        let alive = true;
        axios.get(`${API}/settings/public`)
            .then(r => { if (alive && r.data?.whatsapp_number) setWaNumber(r.data.whatsapp_number); })
            .catch(() => { /* drawer simply omits the link */ });
        return () => { alive = false; };
    }, []);
    const brandLogo = useBrandAsset("brand_logo");

    // Jun 28 2026 — Shrink-on-scroll header (the pattern professional
    // storefronts use). Past a small threshold the header compacts — a
    // shorter bar + smaller logo — so content gets more room while every
    // nav button stays visible and usable.
    const headerRef = useRef(null);
    const [headerScrolled, setHeaderScrolled] = useState(false);
    useEffect(() => {
        let raf = 0;
        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                setHeaderScrolled((prev) => {
                    const next = window.scrollY > 8;
                    return next === prev ? prev : next;
                });
            });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => { window.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
    }, []);
    // Publish the LIVE header height to --app-header-h so every sub-sticky
    // bar / sidebar that sits under the header stays flush in BOTH the full
    // and compact states, across all breakpoints (no gaps, no overlap).
    useEffect(() => {
        const el = headerRef.current;
        if (!el || typeof ResizeObserver === "undefined") return undefined;
        const setVar = () => document.documentElement.style.setProperty("--app-header-h", `${el.offsetHeight}px`);
        setVar();
        const ro = new ResizeObserver(setVar);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        axios.get(`${API}/categories?storefront=true`).then(r => setCats([{ id: "all", name: "All" }, ...r.data])).catch(() => {});
    }, []);

    const close = useCallback(() => setDrawerOpen(false), []);
    // Custom Studio is a focused, full-width premium experience — hide the
    // category sidebar + page padding so the boutique flow can breathe.
    const isCustomStudio = loc.pathname.startsWith("/custom-studio");
    // V4 checkout — full-width canvas (boutique 2-col layout: form left,
    // order rail right) so the sidebar doesn't compete with the rail.
    const isV4Checkout = loc.pathname.startsWith("/custom-checkout/");
    // Order detail pages (readymade + custom) — full-width canvas so the
    // detail page can render its own internal 2-column layout (timeline
    // LEFT, rest of content RIGHT) without competing for room with the
    // account/category sidebar.
    const isOrderDetail = loc.pathname.startsWith("/orders/") || loc.pathname.startsWith("/custom-orders/");
    const isFullBleed = isCustomStudio || isOrderDetail || isV4Checkout;
    // When filter clicks happen anywhere but home, navigate back to home with the filter URL
    const navTo = useCallback((url) => navigate(url), [navigate]);
    // Auto-close the mobile drawer on any navigation — fixes the bug where
    // tapping a submenu item changed the URL but the drawer stayed open,
    // forcing the customer to manually dismiss it.
    useEffect(() => {
        setDrawerOpen(false);
    }, [loc.pathname, loc.search]);
    const filtersWithClose = useMemo(() => ({
        sp,
        setSp: (next, opts) => { setSp(next, opts); close(); },
        navTo: (url) => { navigate(url); close(); },
    }), [sp, setSp, close, navigate]);

    return (
        <div className="min-h-screen cs-boutique-bg text-ink font-body flex flex-col">
            <header ref={headerRef} className={`sticky top-0 z-40 glass-header transition-shadow duration-300 ${headerScrolled ? "shadow-[0_6px_24px_-10px_rgba(107,44,42,0.22)]" : "shadow-none"}`}>
                <div className={`max-w-[90rem] mx-auto px-3 sm:px-6 lg:px-8 flex items-center gap-1.5 sm:gap-3 transition-[height] duration-300 ease-out ${headerScrolled ? "h-14 sm:h-16" : "h-20 sm:h-24"}`}>
                    <button onClick={() => setDrawerOpen(true)} className="lg:hidden p-2 -ml-1 rounded-lg hover:bg-stone-100 flex-shrink-0" data-testid="category-drawer-toggle" aria-label="Menu">
                        <Menu size={20} />
                    </button>

                    <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="shop-brand">
                        <img src={brandLogo || "/jns-logo.png"} alt="JnS Creation" className={`w-auto object-contain transition-[height] duration-300 ease-out ${headerScrolled ? "h-9 sm:h-12 lg:h-12" : "h-12 sm:h-20 lg:h-24"}`} />
                    </Link>

                    {/* Desktop Mega Menu — each department as top-level item */}
                    <div className="hidden lg:flex items-center pl-3 border-l border-stone-200 ml-1">
                        <MegaMenu />
                    </div>

                    {/* Desktop trust strip — three boutique value-props that
                        sit INLINE on the same nav row, filling the empty
                        middle when the mega menu is short. Threshold reads
                        live from /settings/public. */}
                    <HeaderTrustStrip compact={headerScrolled} />

                    {/* Spacer collapses when the trust strip renders (xl+),
                        kept here so smaller screens still push CTAs to the right. */}
                    <div className="flex-1 xl:hidden" />

                    <CustomStudioHeaderCTA />

                    <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
                        {user && <NotificationBell portal="customer" />}
                        <Link to="/wishlist" className="hidden relative p-2.5 rounded-lg hover:bg-ivory-soft text-ink-soft hover:text-ink transition-colors" data-testid="shop-wishlist-icon" aria-label="Wishlist">
                            <Heart size={20} />
                            {wlCount > 0 && (
                                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-wine text-ivory text-[10px] font-bold flex items-center justify-center" data-testid="shop-wishlist-count">{wlCount}</span>
                            )}
                        </Link>
                        <Link to="/cart" className="relative p-2 sm:p-2.5 rounded-lg hover:bg-ivory-soft text-ink-soft hover:text-ink transition-colors flex-shrink-0" data-testid="shop-cart-icon" aria-label="Cart">
                            <ShoppingCart size={20} />
                            {count > 0 && (
                                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-wine text-ivory text-[10px] font-bold flex items-center justify-center">{count}</span>
                            )}
                        </Link>
                        {loading ? <div className="w-9 h-9" /> : user ? (
                            <div className="sm:ml-1 flex-shrink-0"><ProfileMenu user={user} /></div>
                        ) : (
                            <button onClick={openAuthModal} className="inline-flex items-center gap-1.5 px-3 sm:px-3.5 h-9 sm:h-10 btn-primary text-xs sm:text-sm uppercase tracking-wider sm:ml-1 flex-shrink-0" data-testid="shop-login-btn">
                                <LogIn size={14} /> Sign in
                            </button>
                        )}
                    </div>
                </div>
            </header>

            {/* Department nav strip (mobile/tablet) — hidden per launch feedback (clutters above hero);
                mobile users discover depts via the hamburger drawer + tiles on home. */}
            {/* <DepartmentNav variant="strip" /> */}

            {/* Phase 2 (Jul 2026) — promo strip retired: it was the 4th custom
                push on one screen (header pill + drawer link + home teaser).
                One confident CustomStudioTeaser on home replaces it; component
                kept above for future campaigns. */}
            {/* <CustomStudioMobileStrip /> */}

            <div className={isFullBleed
                ? "flex-1 w-full"
                : "max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:flex lg:gap-10 flex-1 w-full"
            }>
                {/* Phase 2 — sidebar hidden on the HOMEPAGE only: with the category
                    carousel + mega menu + search, a third nav column squeezed the
                    hero/product area (triple navigation, per the CRO audit). It
                    stays on department/category/sale/account pages where it earns
                    its keep.
                    Phase 4 — also hidden on /checkout: a category-browse column at
                    the payment step is a classic conversion leak (an exit right
                    when the shopper should be focused on paying). The checkout's
                    own 2-column layout fills the freed width. */}
                {!isFullBleed && loc.pathname !== "/" && loc.pathname !== "/checkout" && (
                    <aside className="hidden lg:block w-60 flex-shrink-0 lg:border-r lg:border-rosegold/15 lg:pr-8">
                        <div className="sticky top-[var(--app-header-h,6rem)]">
                            {isAccountRoute(loc.pathname) ? (
                                <UserMenuPanel currentPath={loc.pathname} user={user} logout={logout} />
                            ) : (
                                // Sidebar shows ONLY departments — Refine/Price filters now live on category pages
                                <DepartmentNav variant="sidebar" />
                            )}
                        </div>
                    </aside>
                )}

                <main className={isFullBleed
                    ? "flex-1 min-w-0 w-full"
                    : "flex-1 min-w-0 pb-24 lg:pb-0"
                } key={loc.pathname}>
                    <div className="fade-up"><Outlet /></div>
                </main>
            </div>

            <Footer />

            <IOSInstallHint />
            <InstallPrompt />

            {/* iter276.1 — Hide the global Maya FAB on the Custom Studio
                journey. Inside Custom Studio, Maya has her OWN dedicated
                presence (header card + welcome moment + consultant panel
                in Step 2). Two Mayas on the same screen confuse the
                customer — one Maya, everywhere. */}
            {!isCustomStudio && <AIStylistFab />}
            <WelcomePopup />
            <CustomStudioFloatingCTA />
            {/* Mobile filter drawer */}
            {drawerOpen && (
                <div className="fixed inset-0 z-[60] lg:hidden" data-testid="category-drawer">
                    <div className="absolute inset-0 bg-stone-900/50" onClick={close} />
                    <aside className="absolute left-0 top-0 bottom-0 w-[82%] max-w-[340px] bg-white shadow-2xl flex flex-col overflow-y-auto fade-up">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 sticky top-0 bg-white">
                            <div className="flex items-center gap-2">
                                <img src={brandLogo || "/jns-logo.png"} alt="JnS" className="w-10 h-10 object-contain" />
                                {/* Jul 2026 — was "Filters": this drawer is the PRIMARY mobile nav
                                    (collections + quick links); filters only join on listing pages. */}
                                <span className="font-display text-xl text-stone-900">Menu</span>
                            </div>
                            <button onClick={close} className="p-1.5 rounded-lg hover:bg-stone-100" data-testid="category-drawer-close" aria-label="Close">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="px-5 py-4">
                            <DepartmentNav variant="sidebar" />
                            {/* Refine + Price only on listing pages (department / category / sale).
                                Home doesn't need them — fewer choices for the customer. */}
                            {(loc.pathname.startsWith("/d/") || loc.pathname.startsWith("/c/") || loc.pathname.startsWith("/sale")) && (
                                <FilterPanel categories={cats} sp={filtersWithClose.sp} setSp={filtersWithClose.setSp} navTo={filtersWithClose.navTo} currentPath={loc.pathname} />
                            )}
                            {/* Jul 2026 — quick links: the drawer is the primary mobile nav, so a
                                customer must be able to reach home, the Custom Studio, her orders /
                                wishlist and a human — without hunting through the footer. */}
                            <div className="mt-4 pt-4 border-t border-stone-200 space-y-0.5" data-testid="drawer-quick-links">
                                <p className="overline mb-2">Quick links</p>
                                <Link to="/" onClick={close} data-testid="drawer-link-home" className="flex items-center gap-2.5 px-3 py-2 text-sm rounded-md text-ink hover:bg-ivory-soft transition-colors">
                                    <HomeIcon size={15} /> Home
                                </Link>
                                <Link to="/custom-studio" onClick={close} data-testid="drawer-link-custom" className="flex items-center gap-2.5 px-3 py-2 text-sm rounded-md text-wine font-semibold hover:bg-ivory-soft transition-colors">
                                    <Sparkles size={15} /> Custom Studio
                                </Link>
                                <Link to="/orders" onClick={close} data-testid="drawer-link-orders" className="flex items-center gap-2.5 px-3 py-2 text-sm rounded-md text-ink hover:bg-ivory-soft transition-colors">
                                    <Package size={15} /> My Orders
                                </Link>
                                <Link to="/wishlist" onClick={close} data-testid="drawer-link-wishlist" className="flex items-center gap-2.5 px-3 py-2 text-sm rounded-md text-ink hover:bg-ivory-soft transition-colors">
                                    <Heart size={15} /> My Wishlist
                                </Link>
                                {waNumber && (
                                    <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" data-testid="drawer-link-whatsapp" className="flex items-center gap-2.5 px-3 py-2 text-sm rounded-md text-ink hover:bg-ivory-soft transition-colors">
                                        <LifeBuoy size={15} className="text-[#25D366]" /> WhatsApp us
                                    </a>
                                )}
                            </div>
                        </div>
                    </aside>
                </div>
            )}

            {/* Mobile bottom nav removed by design — duplicates the header
                icons (Cart / Wishlist / Account) and competes for the
                already-limited mobile viewport with the browser's own URL
                bar + system navigation. Keep all primary actions in the
                sticky header so customers see the full product instead. */}

            <AuthOptionsModal open={authModalOpen} onClose={closeAuthModal} />
            <AddedCrossSellModal />
        </div>
    );
}

// Helper export so ShopHome can compute available sizes consistently
export function computeAvailableSizes(items) {
    const set = new Set();
    for (const it of items) {
        for (const s of getDisplaySizes(it)) {
            if (SIZE_ORDER.includes(s.toUpperCase())) set.add(s.toUpperCase());
        }
    }
    return SIZE_ORDER.filter((s) => set.has(s));
}

const ACCOUNT_ROUTES = ["/profile", "/orders", "/wishlist", "/cart", "/jns-credit", "/notifications", "/account", "/tickets"];
const isAccountRoute = (path) =>
    ACCOUNT_ROUTES.some((p) => path === p || path.startsWith(p + "/"));

function UserMenuPanel({ currentPath, user, logout }) {
    const items = [
        { to: "/profile", label: "My Profile", icon: User },
        { to: "/orders", label: "My Orders", icon: Package },
        { to: "/tickets", label: "My Tickets", icon: LifeBuoy },
        { to: "/wishlist", label: "My Wishlist", icon: Heart },
        { to: "/account/custom-designs", label: "My Custom Outfits", icon: Sparkles },
        { to: "/account/addresses", label: "Address book", icon: MapPin },
    ];
    const handleLogout = async () => { try { await logout?.(); } catch { /* noop */ } };
    return (
        <div data-testid="user-menu-panel">
            {user && (
                <div className="mb-4 pb-4 border-b border-rosegold/15">
                    <p className="text-sm font-semibold text-ink truncate">{user.name || "Welcome"}</p>
                    <p className="text-[11px] text-ink-soft truncate">{user.email || ""}</p>
                </div>
            )}
            <p className="overline mb-3">Your Account</p>
            <nav className="space-y-0.5 mb-6">
                {items.map((i) => {
                    const active = currentPath === i.to || currentPath.startsWith(i.to + "/");
                    return (
                        <Link
                            key={i.to}
                            to={i.to}
                            data-testid={`user-menu-${i.to.replace("/", "")}`}
                            className={`flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                                active
                                    ? "bg-ivory-soft text-ink font-semibold border-l-2 border-rosegold"
                                    : "text-ink-soft hover:bg-ivory-soft hover:text-ink"
                            }`}
                        >
                            <i.icon size={15} /> {i.label}
                        </Link>
                    );
                })}
            </nav>
            <div className="border-t border-rosegold/15 pt-3">
                <button
                    type="button"
                    onClick={handleLogout}
                    data-testid="user-menu-logout"
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-ink-soft hover:bg-ivory-soft hover:text-ink transition-colors"
                >
                    <LogOut size={15} /> Sign out
                </button>
            </div>
        </div>
    );
}

function ProfileMenu({ user }) {
    const { logout } = useAuth();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
    }, [open]);

    const handleLogout = async () => {
        setOpen(false);
        try { await logout?.(); } catch { /* noop */ }
    };

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid="shop-profile-trigger"
                className="flex items-center gap-1.5 px-1.5 sm:px-2 h-10 rounded-lg hover:bg-stone-100 text-sm text-stone-700 flex-shrink-0"
                aria-haspopup="menu"
                aria-expanded={open}
                title="My account"
            >
                {user.picture ? (
                    <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-7 h-7 rounded-full border border-stone-200 flex-shrink-0 object-cover" />
                ) : (
                    <div className="w-7 h-7 rounded-full bg-ivory-soft flex items-center justify-center flex-shrink-0">
                        <User size={14} className="text-rosegold" />
                    </div>
                )}
                <span className="hidden md:inline" data-testid="shop-user-name">{user.name?.split(" ")[0] || "You"}</span>
                <ChevronDown size={13} className={`text-stone-400 transition-transform hidden sm:inline ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute right-0 top-12 w-60 bg-white rounded-xl shadow-2xl border border-stone-200 overflow-hidden z-50 animate-in fade-in slide-in-from-top-1" role="menu" data-testid="shop-profile-menu">
                    <div className="px-4 py-3 border-b border-stone-100 bg-ivory-soft">
                        <p className="text-sm font-semibold text-stone-900 truncate">{user.name || "Welcome"}</p>
                        <p className="text-[11px] text-stone-500 truncate">{user.email || ""}</p>
                    </div>
                    <Link to="/profile" onClick={() => setOpen(false)} data-testid="shop-profile-menu-profile" className="flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 hover:bg-ivory-soft">
                        <User size={15} className="text-stone-500" /> My Profile
                    </Link>
                    <Link to="/orders" onClick={() => setOpen(false)} data-testid="shop-profile-menu-orders" className="flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 hover:bg-ivory-soft">
                        <Package size={15} className="text-stone-500" /> My Orders
                    </Link>
                    <Link to="/tickets" onClick={() => setOpen(false)} data-testid="shop-profile-menu-tickets" className="flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 hover:bg-ivory-soft">
                        <LifeBuoy size={15} className="text-stone-500" /> My Tickets
                    </Link>
                    <Link to="/wishlist" onClick={() => setOpen(false)} data-testid="shop-profile-menu-wishlist" className="flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 hover:bg-ivory-soft">
                        <Heart size={15} className="text-stone-500" /> Wishlist
                    </Link>
                    <Link to="/account/custom-designs" onClick={() => setOpen(false)} data-testid="shop-profile-menu-saved-designs" className="flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 hover:bg-ivory-soft">
                        <Sparkles size={15} className="text-rosegold" /> My Custom Outfits
                    </Link>
                    <div className="border-t border-stone-100">
                        <button
                            type="button"
                            onClick={handleLogout}
                            data-testid="shop-profile-menu-logout"
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                        >
                            <LogOut size={15} /> Sign out
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
